'use client';

import { css, cx } from '@repo/pandacss/styled-system/css';
import { button, type ButtonVariant } from '@repo/pandacss/styled-system/recipes';
import type { CtaType } from '@repo/segment/Analytics/types';
import { LinkType, MonetizableEnum } from '@repo/segment/enums';
import type { AnalyticsType } from '@repo/segment/types';
import { EntityPlacementCtaTypeEnum } from '@repo/supergraph/marketplace/enums';
import { MdLaunch } from 'react-icons/md';
import { AccessibleHiddenText } from '../AccessibleHiddenText';
import { CtaLink } from '../CtaLink';

const MAXIMUM_COLUMNS_MOBILE = 2;

export type CtaButtonProps = {
  /**
   * The CTA configuration object
   */
  cta?: CtaType;
  /**
   * The CTA variant
   */
  variant?: ButtonVariant['variant'];
  /**
   * Analytics payload for tracking
   */
  analyticsPayload: AnalyticsType;
  /**
   * Optional custom styles to override container styling
   */
  className?: string;
  /**
   * Optional flag to indicate if the button should be full width
   */
  fullWidth?: boolean;
  /**
   * Optional string to indicate if federally insured annotation should be shown
   */
  federallyInsuredAnnotation?: string;
  /**
   * Optional index for comparison table columns to control mobile visibility
   */
  columnIndex?: number;
  /**
   * Optional flag to use CD rates specific styling for subtext and federally insured annotation
   */
  useCdRatesStyle?: boolean;
  /**
   * Optional override for the button styles
   */
  buttonClassName?: string;
  /**
   * Optional flag to hide the subtext
   */
  hideSubtext?: boolean;
  /**
   * Optional override for the subtext styles
   */
  subtextClassName?: string;
  /**
   * Optional override for the subtext container styles
   */
  subtextContainerClassName?: string;
};

const outerClass = css({
  display: 'flex',
  flexDirection: 'column',
  columnGap: '1',
  rowGap: '1',
  textAlign: 'center',
});

const outerFullWidthClass = css({ width: 'full' });

const outerMobileHideClass = css({ display: { base: 'none', sm: 'flex' } });

const buttonClass = css({
  whiteSpace: 'nowrap',
  height: '[45px]',
  width: 'full',
  lineHeight: 'xs',
  paddingInlineStart: { base: '2', md: '4' },
  paddingInlineEnd: { base: '2', md: '4' },
  paddingBlockStart: '4',
  paddingBlockEnd: '4',
  letterSpacing: '[0.8125px]',
  justifyContent: 'center',
});

const subtextContainerClass = css({
  display: 'inline',
  justifyContent: 'center',
  alignItems: 'center',
});

const subtextCdRatesContainerClass = css({ width: { base: 'full', sm: 'auto' } });

const subtextTextClass = css({
  textStyle: 'textSmall',
  display: 'inline',
  fontSize: '[11px]',
  marginInlineStart: '1',
  marginInlineEnd: '1',
  color: 'neutral.darker',
  whiteSpace: 'wrap',
});

const subtextDefaultClass = css({
  textStyle: 'textSmall',
  marginRight: '1',
  color: 'neutral.dark',
  whiteSpace: 'wrap',
});

const fdicBoxClass = css({
  backgroundColor: 'yellow.extraLight',
  borderTopLeftRadius: '3',
  borderTopRightRadius: '3',
  borderBottomRightRadius: '3',
  borderBottomLeftRadius: '3',
});

export const CtaButton = ({
  cta,
  variant = 'primary',
  analyticsPayload,
  className,
  fullWidth = false,
  federallyInsuredAnnotation = '',
  columnIndex,
  useCdRatesStyle = false,
  buttonClassName,
  hideSubtext = false,
  subtextClassName,
  subtextContainerClassName,
}: CtaButtonProps) => {
  if (!cta) {
    return null;
  }

  let ctaSubtext = cta.subtext;
  if (cta.subtext && federallyInsuredAnnotation && !useCdRatesStyle) {
    ctaSubtext = `${cta.subtext}, ${federallyInsuredAnnotation}`;
  }

  const isMobileHidden =
    typeof columnIndex === 'number' && columnIndex > MAXIMUM_COLUMNS_MOBILE - 1;

  const showLaunchIcon =
    analyticsPayload.monetizable === MonetizableEnum.yesAssumed ||
    cta.type === EntityPlacementCtaTypeEnum.ApplyNow;

  return (
    <div
      className={cx(
        outerClass,
        fullWidth && outerFullWidthClass,
        isMobileHidden && outerMobileHideClass,
        className,
      )}
      data-testid="cta-container"
    >
      <CtaLink
        cta={cta}
        monetizable={analyticsPayload.monetizable}
        analyticsPayload={analyticsPayload}
        linkType={LinkType.APPLY_NOW_BUTTON}
        className={cx(button({ variant }), buttonClass, buttonClassName)}
        data-testid="cta-button"
      >
        {cta.text}
        {showLaunchIcon && (
          <>
            <AccessibleHiddenText Component="span">on partner's site</AccessibleHiddenText>
            <MdLaunch className={css({ display: 'inline-block', marginLeft: '1' })} />
          </>
        )}
      </CtaLink>
      {((ctaSubtext && !hideSubtext) || (useCdRatesStyle && federallyInsuredAnnotation)) && (
        <div
          className={cx(
            subtextContainerClass,
            useCdRatesStyle && subtextCdRatesContainerClass,
            subtextContainerClassName,
          )}
          data-testid="cta-subtext"
        >
          {useCdRatesStyle ? (
            <>
              {ctaSubtext && (
                <span className={cx(subtextTextClass, subtextClassName)}>
                  {ctaSubtext}
                  {federallyInsuredAnnotation ? ',' : ''}
                </span>
              )}
              {federallyInsuredAnnotation && (
                <div className={fdicBoxClass}>
                  <span className={subtextTextClass}>{federallyInsuredAnnotation}</span>
                </div>
              )}
            </>
          ) : (
            <span className={cx(subtextDefaultClass, subtextClassName)}>{ctaSubtext}</span>
          )}
        </div>
      )}
    </div>
  );
};
